import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  AlertCircle,
  Server,
  Mail,
  CreditCard,
  Globe,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { fetchParametresAdmin, updateParametreAdmin, testEmailAdmin } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import ParametreField, { type ParametreItem } from '../components/ParametreField';
import RetourEnEssai from '../components/RetourEnEssai';

const groupeMeta: Record<string, { label: string; icon: any; description: string }> = {
  devops: {
    label: 'Support technique',
    icon: Server,
    description: 'Configuration d\'infrastructure : messagerie, hébergement, site, paiement en ligne.',
  },
  email: {
    label: 'Messagerie',
    icon: Mail,
    description: 'Envoi d\'emails et destinataires.',
  },
  stripe: {
    label: 'Stripe / Paiement en ligne',
    icon: CreditCard,
    description: 'Clés Stripe et modes de paiement.',
  },
  general: {
    label: 'Général',
    icon: Globe,
    description: 'Adresse du site et autres paramètres généraux.',
  },
  legal: {
    label: 'Mentions légales',
    icon: ShieldCheck,
    description: 'Textes légaux et contacts.',
  },
};

export default function DevopsPage() {
  const { user, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const isSuperAdmin = user?.roles?.includes('ROLE_SUPER_ADMIN');

  const parametresQuery = useQuery({
    queryKey: ['admin-parametres-devops'],
    queryFn: fetchParametresAdmin,
    enabled: isSuperAdmin,
  });

  const updateMutation = useMutation({
    mutationFn: ({ cle, valeur }: { cle: string; valeur: string | null }) => updateParametreAdmin(cle, valeur),
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['admin-parametres-devops'] });
      setSaved(vars.cle);
      setTimeout(() => setSaved(null), 2000);
    },
  });

  const testEmailMutation = useMutation({
    mutationFn: () => testEmailAdmin(testEmail.trim() || undefined),
    onSuccess: (data) => {
      setTestResult({
        type: data.success ? 'success' : 'error',
        message: data.message ?? (data.success ? 'Email envoyé.' : 'Erreur lors de l\'envoi.'),
      });
    },
    onError: (err: any) => {
      setTestResult({ type: 'error', message: err?.message ?? 'Erreur lors de l\'envoi.' });
    },
  });

  if (authLoading || parametresQuery.isPending) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">Cette page est réservée au super-administrateur.</p>
          </div>
        </div>
      </div>
    );
  }

  const groupes: Record<string, any[]> = parametresQuery.data?.groupes ?? {};
  const emailConfigure = parametresQuery.data?.emailConfigure !== false;

  return (
    <div className="p-4 lg:p-0 space-y-5 lg:space-y-6 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Server className="w-7 h-7 text-primary-700" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Support technique</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Configuration d'infrastructure réservée au super-administrateur.
          </p>
        </div>
      </div>

      {!emailConfigure && (
        <div className="card bg-amber-50 border-amber-200 text-amber-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <div className="text-sm">
            <p className="font-semibold">L'envoi d'e-mail est désactivé</p>
            <p className="mt-1">Le paramètre MAILER_DSN est vide ou sur « null://null ».</p>
            <code className="block mt-1 p-2 bg-amber-100 rounded text-xs font-mono break-all">
              smtp://noreply%40mon-association.gest-asso.com:MOT_DE_PASSE@aubergine.o2switch.net:587
            </code>
          </div>
        </div>
      )}

      {/* Essai / production : seul ce compte peut revenir en essai. */}
      <RetourEnEssai />

      {/* Test email */}
      <section className="card space-y-3">
        <h2 className="font-semibold text-gray-900 inline-flex items-center gap-2 dark:text-gray-100">
          <Send className="w-5 h-5 text-primary-700" /> Tester l'envoi d'email
        </h2>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Envoyez un email de test au bureau (ou à l'adresse de votre choix).
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder={emailConfigure ? 'Adresse optionnelle' : "Configurez d'abord MAILER_DSN"}
            disabled={testEmailMutation.isPending || !emailConfigure}
            className="input flex-1"
          />
          <button
            type="button"
            onClick={() => testEmailMutation.mutate()}
            disabled={testEmailMutation.isPending || !emailConfigure}
            className="btn-primary inline-flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {testEmailMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Envoi...</span>
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                <span>Envoyer un test</span>
              </>
            )}
          </button>
        </div>
        {testResult && (
          <div className={`text-sm p-3 rounded ${testResult.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
            {testResult.message}
          </div>
        )}
      </section>

      {parametresQuery.isError && (
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5" />
          <p>Impossible de charger les paramètres. Vérifiez que vous êtes connecté en tant que super-admin.</p>
        </div>
      )}

      <div className="space-y-6">
        {Object.entries(groupes).map(([groupe, parametres]) => {
          const meta = groupeMeta[groupe] ?? { label: groupe, icon: Server, description: '' };
          const Icon = meta.icon;
          return (
            <section key={groupe} className="card lg:p-6 space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-primary-50 text-primary-700">
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-semibold lg:text-lg text-gray-900 dark:text-gray-100">{meta.label}</h2>
                  {meta.description && <p className="text-xs text-gray-500 dark:text-gray-400">{meta.description}</p>}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {parametres.map((p: ParametreItem) => (
                  <ParametreField
                    key={p.cle}
                    parametre={p}
                    onSave={(cle, valeur) => updateMutation.mutate({ cle, valeur })}
                    isSaving={updateMutation.isPending && updateMutation.variables?.cle === p.cle}
                    isSaved={saved === p.cle}
                  />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
